/**
 * LandingCategoryShift — longevity category arrival + facility mosaic.
 */
import { ScanSearch, HeartPulse, Users, Shield } from 'lucide-react';
import { therapyImages } from '../../utils/images';

const PILLARS = [
    { title: 'Biomarker-led wellness', icon: ScanSearch },
    { title: 'Recovery & regeneration', icon: HeartPulse },
    { title: 'Personalized memberships', icon: Users },
    { title: 'Preventive care', icon: Shield },
];

const GALLERY = {
    top: [
        { src: therapyImages.hyperbaric, alt: 'Hyperbaric recovery suite', span: 2, position: 'center' },
        { src: therapyImages.redLight, alt: 'Red light therapy pod', span: 1, position: 'center' },
    ],
    bottom: [
        { src: therapyImages.pemf, alt: 'PEMF therapy session', position: 'center top' },
        { src: therapyImages.infrared, alt: 'Infrared sauna suite', position: 'center' },
        { src: therapyImages.iv, alt: 'IV therapy lounge', position: 'center' },
    ],
};

function MosaicTile({ src, alt, className = '', position = 'center' }) {
    return (
        <div
            className={`relative overflow-hidden rounded-[1.35rem] bg-ink-100 ring-1 ring-ink-100/90 ${className}`}
        >
            <img
                src={src}
                alt={alt}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]"
                style={{ objectPosition: position }}
                loading="lazy"
            />
        </div>
    );
}

function PillarItem({ title, icon: Icon, delay }) {
    return (
        <div
            className="flex items-center gap-3 rounded-xl border border-ink-100 bg-white px-4 py-3.5"
            data-aos="fade-up"
            data-aos-delay={String(delay)}
        >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-teal-50 text-brand-teal-700">
                <Icon className="h-[1.05rem] w-[1.05rem]" strokeWidth={1.85} />
            </span>
            <p className="font-heading text-[0.8125rem] font-semibold leading-snug text-ink-800 sm:text-sm">
                {title}
            </p>
        </div>
    );
}

export default function LandingCategoryShift() {
    return (
        <section
            id="category-shift"
            className="relative scroll-mt-24 overflow-hidden border-t border-ink-100/80 landing-mesh-hero"
        >
            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
                <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14 xl:gap-20">
                    {/* Copy + pillars */}
                    <div>
                        <span className="lp-chip mb-5" data-aos="fade-up">
                            Category shift
                        </span>

                        <h2
                            className="font-heading text-[1.75rem] font-extrabold leading-[1.12] tracking-tight text-ink-900 sm:text-4xl lg:text-[2.5rem]"
                            data-aos="fade-up"
                            data-aos-delay="60"
                        >
                            The longevity category has{' '}
                            <span className="text-brand-teal">arrived in India.</span>
                        </h2>

                        <p
                            className="mt-5 max-w-md text-[0.9375rem] leading-relaxed text-ink-600 sm:text-base"
                            data-aos="fade-up"
                            data-aos-delay="120"
                        >
                            Prevention, recovery and smart-ageing are moving from niche consumption
                            to premium mainstream demand.
                        </p>

                        <div className="mt-7 grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3">
                            {PILLARS.map((pillar, i) => (
                                <PillarItem
                                    key={pillar.title}
                                    title={pillar.title}
                                    icon={pillar.icon}
                                    delay={160 + i * 60}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Photo mosaic */}
                    <div className="space-y-2.5 sm:space-y-3" data-aos="fade-left" data-aos-delay="100">
                        <div className="grid grid-cols-3 gap-2.5 sm:gap-3">
                            {GALLERY.top.map(tile => (
                                <MosaicTile
                                    key={tile.alt}
                                    src={tile.src}
                                    alt={tile.alt}
                                    position={tile.position}
                                    className={`h-[9.5rem] sm:h-[11rem] lg:h-[12.5rem] ${
                                        tile.span === 2 ? 'col-span-2' : 'col-span-1'
                                    }`}
                                />
                            ))}
                        </div>
                        <div className="grid grid-cols-3 gap-2.5 sm:gap-3">
                            {GALLERY.bottom.map(tile => (
                                <MosaicTile
                                    key={tile.alt}
                                    src={tile.src}
                                    alt={tile.alt}
                                    position={tile.position}
                                    className="h-[9rem] sm:h-[10.5rem] lg:h-[11.75rem]"
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Market shift callout */}
                <div
                    className="mt-12 rounded-[1.75rem] border-2 border-brand-teal/35 bg-white/70 px-6 py-6 text-center sm:mt-14 sm:px-10 sm:py-7 lg:mt-16"
                    data-aos="fade-up"
                    data-aos-delay="120"
                >
                    <p className="mx-auto max-w-3xl text-base leading-relaxed text-ink-600 sm:text-lg">
                        The market is shifting from{' '}
                        <span className="font-heading font-bold text-brand-teal">treatment after illness</span>
                        {' '}to{' '}
                        <span className="font-heading font-bold text-brand-teal">measurable wellness</span>
                        {' '}before decline.
                    </p>
                </div>
            </div>
        </section>
    );
}
